import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import LanguageToggle from "@/components/LanguageToggle";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, AlertCircle, CheckCircle2, X } from "lucide-react";

const LoginPage = () => {
  const { language } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const bn = language === "bn";

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.error) {
        setError(bn ? "লগইন ব্যর্থ হয়েছে। আবার চেষ্টা করুন।" : result.error);
      }
      // Navigation handled by AuthContext + GuestRoute redirect
    } catch {
      setError(bn ? "লগইন ব্যর্থ হয়েছে। আবার চেষ্টা করুন।" : "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError("");
    setForgotLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        setForgotError(bn ? "ইমেইল পাঠাতে ব্যর্থ হয়েছে।" : error.message);
      } else {
        setForgotSuccess(true);
      }
    } catch {
      setForgotError(bn ? "কিছু একটা সমস্যা হয়েছে।" : "Something went wrong. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSocialLogin = async (provider: "google" | "apple") => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      setError(bn ? "সোশ্যাল লগইন ব্যর্থ" : "Social login failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfaf7] px-4 py-5 sm:px-6 sm:py-8">
      <div className="relative mx-auto flex w-full max-w-3xl flex-col overflow-hidden rounded-t-[32px] bg-[#f0f8f4] px-5 pb-20 pt-7 shadow-float sm:px-8 sm:pt-9">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#e5f2ed]" />
        <div className="absolute bottom-4 -left-8 h-28 w-28 rounded-full bg-[#e5f2ed]/70" />
        <div className="relative z-10 mb-6 flex justify-end">
          <LanguageToggle />
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-col items-center">
          <img src="/logo.png" alt="Sebaco" className="mb-3 h-20 w-20 rounded-[24px] border border-[#dfe9e5] bg-white p-2 object-contain" />
          <h1 className="font-serif text-3xl font-extrabold leading-tight text-[#18544e]">
            {bn ? "স্বাগতম!" : "Welcome!"}
          </h1>
          <p className="mt-1 text-sm text-[#697273]">
            {bn ? "Sebaco-তে লগইন করুন" : "Sign in to Sebaco"}
          </p>
        </motion.div>
      </div>

      <motion.div className="relative z-10 mx-auto mt-0 w-full max-w-3xl px-1" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}>
        <div className="soft-surface rounded-[28px] p-5 sm:p-7">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-bold text-[#263449]">{bn ? "ইমেইল" : "Email"}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#287d73]" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={bn ? "আপনার ইমেইল" : "your@email.com"} className="w-full rounded-2xl border border-[#dfe9e5] bg-white py-3 pl-10 pr-4 text-sm font-medium text-foreground outline-none placeholder:text-[#7a828e] focus:border-[#2c8880] focus:ring-0" required />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold text-[#263449]">{bn ? "পাসওয়ার্ড" : "Password"}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#287d73]" />
                <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="w-full rounded-2xl border border-[#dfe9e5] bg-white py-3 pl-10 pr-12 text-sm font-medium text-foreground outline-none placeholder:text-[#7a828e] focus:border-[#2c8880] focus:ring-0" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                  {showPassword ? <EyeOff className="h-4 w-4 text-[#287d73]" /> : <Eye className="h-4 w-4 text-[#287d73]" />}
                </button>
              </div>
            </div>
            {error && (
              <div className="flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-2.5 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}
            <div className="text-right">
              <button type="button" onClick={() => { setShowForgot(!showForgot); setForgotSuccess(false); setForgotError(""); setForgotEmail(""); }} className="text-xs font-bold text-[#287d73]">
                {bn ? "পাসওয়ার্ড ভুলে গেছেন?" : "Forgot Password?"}
              </button>
            </div>

            <AnimatePresence>
              {showForgot && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-foreground">{bn ? "পাসওয়ার্ড রিসেট করুন" : "Reset your password"}</p>
                      <button type="button" onClick={() => setShowForgot(false)}><X className="h-3.5 w-3.5 text-muted-foreground" /></button>
                    </div>

                    {forgotSuccess ? (
                      <div className="flex items-start gap-2 text-green-600">
                        <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                        <p className="text-xs font-medium">
                          {bn ? "রিসেট লিংক পাঠানো হয়েছে! আপনার ইমেইল চেক করুন।" : "Reset link sent! Check your email inbox and click the link to set a new password."}
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={handleForgotPassword} className="space-y-2.5">
                        <p className="text-[11px] text-muted-foreground">
                          {bn ? "আপনার ইমেইল লিখুন, আমরা একটি রিসেট লিংক পাঠাবো।" : "Enter your account email and we'll send you a reset link."}
                        </p>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                          <input
                            type="email"
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            placeholder={bn ? "আপনার ইমেইল" : "your@email.com"}
                            className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-background text-foreground text-xs font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground"
                            required
                          />
                        </div>
                        {forgotError && (
                          <div className="flex items-center gap-1.5 text-destructive text-[11px]">
                            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                            {forgotError}
                          </div>
                        )}
                        <motion.button
                          type="submit"
                          disabled={forgotLoading}
                          whileTap={{ scale: 0.97 }}
                          className="w-full gradient-primary text-primary-foreground py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-60"
                        >
                          {forgotLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                          {bn ? "রিসেট লিংক পাঠান" : "Send Reset Link"}
                        </motion.button>
                      </form>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <motion.button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2c8880] py-3.5 text-sm font-bold text-white disabled:opacity-60" whileTap={{ scale: 0.97 }}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {bn ? "লগইন" : "Sign In"}
              {!loading && <ArrowRight className="h-4 w-4" />}
            </motion.button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[10px] text-muted-foreground font-bold">{bn ? "অথবা" : "OR"}</span>
            <div className="flex-1 h-px bg-border" />
          </div>
           <div className="flex gap-4">
              <button onClick={() => handleSocialLogin("apple")} className="flex h-[62px] flex-1 items-center justify-center gap-2 rounded-full bg-[#edf5f1] text-base font-bold text-[#18544e]">
               <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              Apple
            </button>
              <button onClick={() => handleSocialLogin("google")} className="flex h-[62px] flex-1 items-center justify-center gap-2 rounded-full bg-[#edf5f1] text-base font-bold text-[#18544e]">
               <svg className="h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="currentColor"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="currentColor"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="currentColor"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="currentColor"/></svg>
              Google
            </button>
          </div>
        </div>
        <p className="text-center mt-6 text-sm text-muted-foreground">
          {bn ? "অ্যাকাউন্ট নেই? " : "Don't have an account? "}
          <Link to="/signup" className="font-bold text-primary">{bn ? "সাইন আপ" : "Sign Up"}</Link>
        </p>
      </motion.div>
    </div>
  );
};

export default LoginPage;
